import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, Loader2, XCircle } from 'lucide-react';
import { useChatStore } from '../stores/chat';
import { Assistant } from '../types';
import defaultBotAvatar from '../assets/images/default-bot-avatar.png';
import { useTranslation } from 'react-i18next';

export default function NewChat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    createThread,
    assistants,
    fetchAssistants,
    selectedAssistant,
    setSelectedAssistant
  } = useChatStore();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setSelectedAssistant(null);
        await fetchAssistants();
      } catch (error: any) {
        console.error('Failed to load data:', error);
        setError(error?.response?.data?.detail || t('new_chat.error.load_failed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [fetchAssistants, setSelectedAssistant]);

  const handleCreateChat = async (assistant: Assistant) => {
    if (isCreating) return;

    setIsCreating(true);
    try {
      const threadId = await createThread(
        assistant.id
      );
      navigate(`/chat/${threadId}`);
    } catch (error: any) {
      console.error('Failed to create chat:', error);
      setError(error?.response?.data?.detail || t('new_chat.error.create_failed'));
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#0F1524]">
        <p className="text-white">{t('new_chat.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center justify-center text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-[#1A2235] flex items-center justify-center mb-4">
            <XCircle size={24} className="text-[#EF4444]" />
          </div>
          <p className="text-[#EF4444] mb-4 text-lg font-medium">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-[#1A2235] text-white hover:bg-[#253045] transition-colors"
          >
            {t('chat.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!assistants || assistants.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[#0F1524]">
        <p className="text-white">{t('new_chat.error.no_assistants')}</p>
      </div>
    );
  }

  // If assistant is selected, show call to action
  if (selectedAssistant) {
    return (
      <div className="h-full p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-8">
          </div>

          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex items-start gap-4 p-6 rounded-[24px] bg-transparent mb-8 max-w-md"
              style={{
                position: 'relative',
                backgroundClip: 'padding-box',
                borderRadius: '24px',
                border: '1px solid transparent',
                boxShadow: '0px 1.3px 32.7px rgba(0, 0, 0, 0.15)',
                background: 'linear-gradient(#0F1524, #0F1524) padding-box, linear-gradient(to bottom, rgba(255,255,255,0.5), rgba(0,194,203,0.8)) border-box'
              }}
            >
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden bg-gradient-to-br from-[#D6E2E8] to-[#ECF3F5] flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundImage: 'linear-gradient(146.84deg, #D6E2E8 -17.54%, #ECF3F5 80.24%)',
                  filter: 'drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))'
                }}
              >
                <img src={defaultBotAvatar} alt="Bot Avatar" className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="h-7">
                  <span className="text-[#00C0FF] text-[16px] font-medium truncate block">
                    {selectedAssistant.name || 'Universal assistant'}
                  </span>
                </div>
                <div className="mt-2">
                  <p className="text-[14px] font-light text-white/90 leading-relaxed">
                    {selectedAssistant.description || 'I will help you with many issues, you can just start writing'}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleCreateChat(selectedAssistant)}
              disabled={isCreating}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#00C2CB] to-[#6A98F0] text-[#0F1524] hover:opacity-90 transition-colors disabled:opacity-50 shadow-[0px_1.3px_5px_rgba(0,0,0,0.25)]"
            >
              <Plus size={20} />
              <span className="font-medium">{t('new_chat.create_chat')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show assistant selection
  return (
    <div className="h-full p-4 pt-2 overflow-y-auto">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 mt-0 pl-1">
          <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#00C2CB] to-[#6A98F0]">
            {t('new_chat.title')}
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#00C2CB]" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <XCircle className="w-12 h-12 text-red-500 mb-4" />
            <p className="text-lg font-medium text-white mb-2">{t('new_chat.error.load_failed')}</p>
            <p className="text-sm text-gray-400">{t('new_chat.error.contact_admin')}</p>
          </div>
        ) : assistants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bot className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium text-white mb-2">{t('new_chat.error.no_assistants')}</p>
            <p className="text-sm text-gray-400">{t('new_chat.error.contact_admin')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {assistants.map((assistant) => (
              <button
                key={assistant.id}
                onClick={() => setSelectedAssistant(assistant)}
                className="flex flex-col p-3 rounded-[16px] bg-transparent transition-colors hover:scale-105"
                style={{
                  position: 'relative',
                  backgroundClip: 'padding-box',
                  borderRadius: '16px',
                  border: '1px solid transparent',
                  boxShadow: '0px 1.3px 32.7px rgba(0, 0, 0, 0.15)',
                  background: 'linear-gradient(#0F1524, #0F1524) padding-box, linear-gradient(to bottom, rgba(255,255,255,0.5), rgba(0,194,203,0.8)) border-box'
                }}
              >
                <div className="flex items-start gap-3 text-left h-full">
                  <div className="w-[50px] h-[50px] rounded-full overflow-hidden bg-gradient-to-br from-[#D6E2E8] to-[#ECF3F5] flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundImage: 'linear-gradient(146.84deg, #D6E2E8 -17.54%, #ECF3F5 80.24%)',
                      filter: 'drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))'
                    }}
                  >
                    <img src={defaultBotAvatar} alt="Bot Avatar" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0 h-full">
                    <div className="h-6">
                      <span className="text-[#00C0FF] text-[14px] font-medium truncate block">
                        {assistant.name || t('chat.universal_assistant')}
                      </span>
                    </div>
                    <div className="mt-1 flex-1">
                      <p className="text-[11px] font-light text-white/80 leading-tight line-clamp-3">
                        {assistant.description || t('chat.default_description')}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
